import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useRouterState } from '@tanstack/react-router'
import { ChallengeService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'
import { useAuth } from './useAuth'
import { queryClient } from '@/modules/queryClient'
import { useSetAtom } from 'jotai'
import { youHaveAssignmentsOrganizationNameAtom } from '@/store/userAtoms'
import { useLocalSettings } from './useLocalSettings'

export const ongoingChallengesQueryKeys = {
  all: () => ['ongoing-challenges'] as const,
  user: () => ['ongoing-challenges', 'user'] as const
}

const fetchOngoingChallenges = async (): Promise<ChallengeService.ChallengesResponse> => {
  try {
    const response = await apis.challengeService.getOngoingChallenges()
    return response.data
  } catch {
    return {
      availableChallenges: [],
      availableAssignments: [],
      count: 0,
    }
  }
}

export const useOngoingChallenges = () => {
  const { isAuthenticated, user } = useAuth()
  // const setIsYouHaveAssignmentsDialogOpen = useSetAtom(isYouHaveAssignmentsDialogOpenAtom)
  const setYouHaveAssignmentsOrganizationName = useSetAtom(youHaveAssignmentsOrganizationNameAtom)
  const routerState = useRouterState()
  const { getSetting, setSetting } = useLocalSettings()

  const query = useQuery({
    queryKey: ongoingChallengesQueryKeys.user(),
    queryFn: fetchOngoingChallenges,
    enabled: isAuthenticated,
    staleTime: 30 * 60 * 1000, // 30 minutes - shorter than default since challenges can change frequently
  })

  useEffect(() => {
    const availableAssignments = query.data?.availableAssignments ?? []
    const currentPath = routerState.location.pathname
    const isAssignmentsPage = currentPath.startsWith('/assignments')
    const lastLoginAt = user?.lastLoginAt ?? null
    const dismissedForLogin = getSetting('dismissedGoToAssignments')

    if (
      availableAssignments.length > 0 &&
      !query.isPending &&
      !query.isFetching &&
      !isAssignmentsPage &&
      lastLoginAt &&
      dismissedForLogin !== lastLoginAt
    ) {
      const firstAssignment = availableAssignments[0]
      if (firstAssignment?.organizationName) {
        setYouHaveAssignmentsOrganizationName(firstAssignment.organizationName)
      } else {
        setYouHaveAssignmentsOrganizationName(null)
      }
      // setIsYouHaveAssignmentsDialogOpen(true)
    }
  }, [
    query.data?.availableAssignments,
    query.isPending,
    query.isFetching,
    routerState.location.pathname,
    user?.lastLoginAt,
    getSetting,
    setSetting,
    // setIsYouHaveAssignmentsDialogOpen,
    setYouHaveAssignmentsOrganizationName,
  ])

  return {
    challenges: query.data?.availableChallenges ?? [],
    assignments: query.data?.availableAssignments ?? [],
    hasFinishedAssignment: query.data?.hasFinishedAssignment ?? false,
    count: query.data?.count ?? 0,
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    clearOngoingChallenges: () => {
      queryClient.removeQueries({ queryKey: ongoingChallengesQueryKeys.all() })
    }
  }
}
